import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CommandeFournisseurRecord,
  CommandeFournisseurStatut,
  commandeFournisseurApiService,
} from '../services/CommandeFournisseurApiService';

type StatutOption = 'tous' | CommandeFournisseurStatut;

const STATUT_OPTIONS: Array<{ value: StatutOption; label: string }> = [
  { value: 'tous', label: 'Tous les statuts' },
  { value: 'BROUILLON', label: '📝 Brouillon' },
  { value: 'ENVOYEE', label: '📤 Envoyée' },
  { value: 'RECUE', label: '✅ Reçue' },
  { value: 'ANNULEE', label: '✖ Annulée' },
];

const statutBadge = (statut: CommandeFournisseurStatut) => {
  switch (statut) {
    case 'BROUILLON':
      return 'bg-blue-100 text-blue-800';
    case 'ENVOYEE':
      return 'bg-yellow-100 text-yellow-800';
    case 'RECUE':
      return 'bg-green-100 text-green-800';
    case 'ANNULEE':
      return 'bg-red-100 text-red-800';
    case 'GENEREE':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMontant = (montant?: number | null) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant || 0);

const buildCsv = (rows: CommandeFournisseurRecord[]) => {
  let csv = '\uFEFF';
  csv += 'Numero commande;Date;Statut;Montant;Devis\n';
  rows.forEach((row) => {
    const montant = row.totaux?.montantTotal ?? row.total_ht ?? 0;
    const devis = (row.devisInclus || []).join(', ');
    const date = formatDate(row.dateCreation || row.created_at || undefined);
    csv += `"${row.numeroCommande}";"${date}";"${row.statut}";${montant};"${devis}"\n`;
  });
  return csv;
};

const CommandesFournisseur: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commandes, setCommandes] = useState<CommandeFournisseurRecord[]>([]);
  const [recherche, setRecherche] = useState(searchParams.get('q') || '');
  const [statut, setStatut] = useState<StatutOption>((searchParams.get('statut') as StatutOption) || 'tous');

  const devisId = searchParams.get('devisId') || '';
  const devisRef = searchParams.get('devisRef') || '';

  const fetchCommandes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await commandeFournisseurApiService.listerCommandes({
        q: searchParams.get('q') || undefined,
        statut: searchParams.get('statut') || undefined,
        devisId: devisId || undefined,
        devisRef: devisRef || undefined,
        limit: 100,
        offset: 0,
      });
      setCommandes(data);
    } catch (err: any) {
      console.error('Erreur chargement commandes fournisseur:', err);
      setError(err?.message || 'Erreur lors du chargement des commandes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchCommandes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  useEffect(() => {
    setRecherche(searchParams.get('q') || '');
    setStatut((searchParams.get('statut') as StatutOption) || 'tous');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateParams = (params: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  const commandesAffichees = useMemo(() => commandes, [commandes]);

  const handleExport = () => {
    const csv = buildCsv(commandesAffichees);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `commandes_fournisseur_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleMajStatut = async (commandeId: string, nouveauStatut: CommandeFournisseurStatut) => {
    try {
      const updated = await commandeFournisseurApiService.majStatut(commandeId, nouveauStatut);
      setCommandes((prev) =>
        prev.map((c) => (c.id === commandeId ? { ...c, ...updated, statut: updated.statut } : c)),
      );
    } catch (err: any) {
      console.error('Erreur mise à jour statut commande fournisseur:', err);
      alert(err?.message || 'Impossible de mettre à jour le statut.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Commandes fournisseurs</h1>
          <p className="text-sm text-gray-500">
            Liste complète des commandes fournisseurs (lecture seule, filtrable par statut/devis).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
            disabled={commandesAffichees.length === 0}
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Exporter CSV
          </button>
          <button
            type="button"
            onClick={fetchCommandes}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
          >
            Recharger
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">Recherche</label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                className="input-field w-full"
                placeholder="Rechercher par numéro, devis ou notes..."
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    updateParams({ q: recherche || null });
                  }
                }}
              />
              <button
                type="button"
                className="btn-primary"
                onClick={() => updateParams({ q: recherche || null })}
              >
                Rechercher
              </button>
              {recherche && (
                <button
                  type="button"
                  className="bg-gray-100 px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-200"
                  onClick={() => {
                    setRecherche('');
                    updateParams({ q: null });
                  }}
                  title="Effacer la recherche"
                >
                  Effacer
                </button>
              )}
            </div>
          </div>
          <div className="w-full md:w-64">
            <label className="block text-sm font-medium text-gray-700">Statut</label>
            <select
              className="input-field w-full"
              value={statut}
              onChange={(e) => {
                const value = e.target.value as StatutOption;
                setStatut(value);
                updateParams({ statut: value === 'tous' ? null : value });
              }}
            >
              {STATUT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {(devisId || devisRef) && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 border border-primary-200">
              Filtré sur devis {devisRef || devisId}
            </span>
            <button
              type="button"
              className="text-sm text-primary-600 hover:text-primary-800"
              onClick={() => updateParams({ devisId: null, devisRef: null })}
            >
              Retirer le filtre
            </button>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement des commandes...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">{error}</div>
        ) : commandesAffichees.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucune commande trouvée avec ces filtres.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Numéro commande
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Devis
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {commandesAffichees.map((commande) => {
                  const devisRefs = commande.devisInclus || [];
                  const montant = commande.totaux?.montantTotal ?? commande.total_ht ?? 0;
                  const date = commande.dateCreation || commande.created_at || undefined;
                  return (
                    <tr key={commande.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {commande.numeroCommande || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statutBadge(
                            commande.statut,
                          )}`}
                        >
                          {commande.statut}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex flex-wrap gap-1">
                          {devisRefs.slice(0, 3).map((ref, index) => (
                            <span
                              key={`${commande.id}-devis-${index}`}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {ref}
                            </span>
                          ))}
                          {devisRefs.length > 3 && (
                            <span className="text-xs text-gray-500">+{devisRefs.length - 3} autres</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatMontant(montant)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                        <Link to={`/commandes-fournisseur/${commande.id}`} className="text-primary-600 hover:text-primary-800">
                          Ouvrir
                        </Link>
                        <select
                          value={commande.statut}
                          onChange={(e) =>
                            handleMajStatut(commande.id, e.target.value as CommandeFournisseurStatut)
                          }
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                        >
                          {STATUT_OPTIONS.filter((opt) => opt.value !== 'tous').map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-500">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-primary-600 hover:text-primary-800 underline"
        >
          Retour
        </button>
      </div>
    </div>
  );
};

export default CommandesFournisseur;
